import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  GetApp as GetAppIcon,
  MoreVert as MoreVertIcon,
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import { useAuth } from '../App';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

/** Maison affichée / filtrée : d’abord celle sur la facture, sinon celle du client */
function effectiveMaisonRepos(facture) {
  const f = facture.maison_repos != null ? String(facture.maison_repos).trim() : '';
  if (f) return f;
  const c = facture.client_maison != null ? String(facture.client_maison).trim() : '';
  return c;
}

function Factures() {
  const { user } = useAuth();
  const [factures, setFactures] = useState([]);
  const [filteredFactures, setFilteredFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [filterMaison, setFilterMaison] = useState('');
  const [maisonFilterChoices, setMaisonFilterChoices] = useState([]);
  // Variables d'état pour le menu de statut supprimées (colonne statut n'existe plus)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, facture: null });
  const [recapDialog, setRecapDialog] = useState(false);
  const [recapData, setRecapData] = useState({
    periode: 'mois',
    date: new Date(),
    mois: new Date().getMonth(),
    annee: new Date().getFullYear(),
  });
  const [monthlyInvoiceDate, setMonthlyInvoiceDate] = useState(new Date());
  const [monthlyInvoiceMaison, setMonthlyInvoiceMaison] = useState('');
  const [monthlyInvoiceDialog, setMonthlyInvoiceDialog] = useState(false);
  const [monthlyInvoiceStats, setMonthlyInvoiceStats] = useState(null);
  const [monthlyMaisonsOptions, setMonthlyMaisonsOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  // Constante statuts supprimée (colonne statut n'existe plus)

  const periodes = [
    { value: 'semaine', label: 'Semaine' },
    { value: 'mois', label: 'Mois' },
    { value: 'annee', label: 'Année' },
  ];

  const moisOptions = [
    { value: 0, label: 'Janvier' },
    { value: 1, label: 'Février' },
    { value: 2, label: 'Mars' },
    { value: 3, label: 'Avril' },
    { value: 4, label: 'Mai' },
    { value: 5, label: 'Juin' },
    { value: 6, label: 'Juillet' },
    { value: 7, label: 'Août' },
    { value: 8, label: 'Septembre' },
    { value: 9, label: 'Octobre' },
    { value: 10, label: 'Novembre' },
    { value: 11, label: 'Décembre' },
  ];

  useEffect(() => {
    fetchFactures();
  }, []);

  useEffect(() => {
    if (!monthlyInvoiceDialog) {
      setMonthlyInvoiceStats(null);
      return;
    }
    if (!monthlyInvoiceDate) return;
    const dateStr = format(monthlyInvoiceDate, 'yyyy-MM');
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, mrRes] = await Promise.all([
          axios.get(`/api/monthly-invoice-stats?date=${encodeURIComponent(dateStr)}`),
          axios.get('/api/maisons-repos'),
        ]);
        if (cancelled) return;
        setMonthlyInvoiceStats(statsRes.data || null);
        const names = (Array.isArray(mrRes.data) ? mrRes.data : [])
          .map((m) => (m && m.nom ? String(m.nom).trim() : ''))
          .filter(Boolean)
          // Exclusion volontaire de "Notre foyer" des menus déroulants de sélection
          .filter((n) => n.toLowerCase() !== 'notre foyer');
        setMonthlyMaisonsOptions(names.length ? names : ['Les Charmilles']);
      } catch (e) {
        if (!cancelled) {
          setMonthlyInvoiceStats(null);
          setMonthlyMaisonsOptions(['Les Charmilles']);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [monthlyInvoiceDialog, monthlyInvoiceDate]);

  const fetchFactures = async () => {
    try {
      const response = await axios.get('/api/factures');
      const rows = Array.isArray(response.data) ? response.data : [];
      if (!Array.isArray(response.data)) {
        console.error('Réponse /api/factures inattendue (pas un tableau):', response.data);
        showSnackbar(
          'Réponse serveur incorrecte pour la liste des factures. Vérifiez la console ou reconnectez-vous.',
          'error'
        );
      }
      setFactures(rows);

      // Exclusion volontaire de "Notre foyer" des menus déroulants de sélection
      const excludeNotreFoyer = (n) => String(n || '').trim().toLowerCase() !== 'notre foyer';
      try {
        const mrRes = await axios.get('/api/maisons-repos');
        const apiNames = (Array.isArray(mrRes.data) ? mrRes.data : [])
          .map((m) => (m && m.nom ? String(m.nom).trim() : ''))
          .filter(Boolean);
        const fromInvoices = new Set();
        rows.forEach((f) => {
          const m = effectiveMaisonRepos(f);
          if (m) fromInvoices.add(m);
        });
        const merged = [...new Set([...apiNames, ...fromInvoices])]
          .filter(excludeNotreFoyer)
          .sort((a, b) => a.localeCompare(b, 'fr'));
        setMaisonFilterChoices(merged);
      } catch {
        const fromInvoices = new Set();
        rows.forEach((f) => {
          const m = effectiveMaisonRepos(f);
          if (m) fromInvoices.add(m);
        });
        setMaisonFilterChoices(
          [...fromInvoices].filter(excludeNotreFoyer).sort((a, b) => a.localeCompare(b, 'fr'))
        );
      }

      // Créer les options de recherche
      const options = [];
      rows.forEach((facture) => {
        // Ajouter le numéro de facture
        if (facture.numero) {
          options.push({
            label: facture.numero,
            type: 'numero',
            facture: facture
          });
        }
        
        // Ajouter le nom du client
        const clientName = facture.client_prenom ? 
          `${facture.client_prenom} ${facture.client_nom}` : 
          facture.client_nom;
        if (clientName) {
          // Éviter les doublons de noms de clients
          const existingClient = options.find(opt => opt.label === clientName && opt.type === 'client');
          if (!existingClient) {
            options.push({
              label: clientName,
              type: 'client',
              facture: facture
            });
          }
        }
      });
      
      setSearchOptions(options);
    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
      const status = error.response?.status;
      if (status === 401) {
        showSnackbar('Session expirée : reconnectez-vous pour voir les factures.', 'error');
      } else {
        showSnackbar(
          error.response?.data?.error || 'Impossible de charger les factures (réseau ou serveur).',
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonction de filtrage (recherche texte + maison)
  const filterFactures = (factures, searchTerm, maisonKey) => {
    if (!Array.isArray(factures)) {
      return [];
    }
    let list = factures;
    if (maisonKey === '__EMPTY__') {
      list = list.filter((f) => !effectiveMaisonRepos(f));
    } else if (maisonKey) {
      list = list.filter((f) => effectiveMaisonRepos(f) === maisonKey);
    }

    if (!searchTerm.trim()) {
      return list;
    }

    const term = searchTerm.toLowerCase().trim();
    return list.filter((facture) => {
      const n = facture.numero != null ? String(facture.numero).toLowerCase() : '';
      const c = facture.client != null ? String(facture.client).toLowerCase() : '';
      const cn = facture.client_nom != null ? String(facture.client_nom).toLowerCase() : '';
      const cp = facture.client_prenom != null ? String(facture.client_prenom).toLowerCase() : '';
      const full = cp && cn ? `${cp} ${cn}` : cn || cp;
      const maison = effectiveMaisonRepos(facture).toLowerCase();
      return (
        n.includes(term) ||
        c.includes(term) ||
        cn.includes(term) ||
        cp.includes(term) ||
        full.includes(term) ||
        maison.includes(term)
      );
    });
  };

  // Effet pour filtrer les factures quand le terme de recherche ou la maison change
  useEffect(() => {
    const filtered = filterFactures(factures, searchTerm, filterMaison);
    setFilteredFactures(filtered);
  }, [factures, searchTerm, filterMaison]);

  // Gestionnaire pour le changement du terme de recherche
  const handleSearchChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      setSearchTerm(newValue);
    } else if (newValue && newValue.label) {
      setSearchTerm(newValue.label);
    } else {
      setSearchTerm('');
    }
  };

  const handleViewFacture = (id) => {
    navigate(`/factures/${id}`);
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await axios.get(`/api/factures/${id}/pdf`, {
        responseType: 'blob',
      });
      
      // Extraire le nom de fichier depuis les headers de la réponse
      const contentDisposition = response.headers['content-disposition'];
      let filename = `facture-${id}.pdf`; // Nom par défaut
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  const handleMonthlyInvoiceClick = () => {
    setMonthlyInvoiceDialog(true);
  };

  const handleDownloadMonthlyInvoice = async () => {
    try {
      console.log('=== DÉBUT TÉLÉCHARGEMENT FACTURE MENSUELLE ===');
      const dateStr = format(monthlyInvoiceDate, 'yyyy-MM');
      console.log('Mois sélectionné:', dateStr);
      console.log('Maison sélectionnée:', monthlyInvoiceMaison);
      
      // Construire l'URL avec les paramètres
      let url = `/api/monthly-invoice-pdf?date=${dateStr}`;
      if (monthlyInvoiceMaison) {
        url += `&maison=${encodeURIComponent(monthlyInvoiceMaison)}`;
      }
      
      console.log('Appel API vers:', url);
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      console.log('Réponse reçue:', response.status, response.headers);
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `facture_mensuelle_${dateStr}.pdf`;
      if (monthlyInvoiceMaison) {
        filename = `facture_mensuelle_${monthlyInvoiceMaison}_${dateStr}.pdf`;
      }
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = urlBlob;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlBlob);
      
      console.log('Téléchargement réussi!');
      showSnackbar('Facture mensuelle téléchargée avec succès', 'success');
      setMonthlyInvoiceDialog(false);
    } catch (error) {
      console.error('=== ERREUR LORS DU TÉLÉCHARGEMENT ===');
      console.error('Erreur complète:', error);
      console.error('Message:', error.message);
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
      let detail = '';
      const data = error.response?.data;
      const status = error.response?.status;
      try {
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const j = JSON.parse(text);
            detail = j.error || j.message || text;
          } catch {
            detail = text;
          }
        } else if (data && typeof data === 'object' && data.error) {
          detail = data.error;
        }
      } catch (e) {
        /* ignore */
      }
      if (detail) {
        showSnackbar(detail, 'error');
      } else if (status === 404) {
        showSnackbar(
          'Aucune facture pour ce mois (ou pour cette maison). Créez d’abord des factures sur la période choisie.',
          'error'
        );
      } else {
        showSnackbar('Erreur lors du téléchargement de la facture mensuelle', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fonctions de gestion de la suppression
  const handleDeleteDialogOpen = (facture) => {
    setDeleteDialog({ open: true, facture });
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialog({ open: false, facture: null });
  };

  const handleDeleteFacture = async () => {
    try {
      await axios.delete(`/api/factures/${deleteDialog.facture.id}`);
      
      // Mettre à jour la liste des factures
      setFactures(prev => prev.filter(f => f.id !== deleteDialog.facture.id));
      setFilteredFactures(prev => prev.filter(f => f.id !== deleteDialog.facture.id));
      
      showSnackbar('Facture supprimée avec succès', 'success');
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la suppression de la facture';
      showSnackbar(errorMessage, 'error');
    }
  };

  // Vérifier si l'utilisateur peut supprimer des factures
  const canDeleteFactures = user?.role === 'admin' || user?.role === 'superadmin';

  const handleRecapDialogOpen = () => {
    setRecapDialog(true);
  };

  const handleRecapDialogClose = () => {
    setRecapDialog(false);
  };

  const handleRecapDataChange = (field, value) => {
    setRecapData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateRecap = async () => {
    try {
      let dateStr;
      
      // Si la période est "mois", utiliser le mois et l'année sélectionnés
      if (recapData.periode === 'mois') {
        const date = new Date(recapData.annee, recapData.mois, 1);
        dateStr = format(date, 'yyyy-MM-dd');
      } else {
        dateStr = format(recapData.date, 'yyyy-MM-dd');
      }
      
      const response = await axios.get(`/api/factures/recapitulatif/${recapData.periode}?date=${dateStr}`, {
        responseType: 'blob',
      });
      
      // Extraire le nom de fichier depuis les headers de la réponse
      const contentDisposition = response.headers['content-disposition'];
      let filename = `recapitulatif-${recapData.periode}-${dateStr}.pdf`; // Nom par défaut
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      handleRecapDialogClose();
    } catch (error) {
      console.error('Erreur lors de la génération du récapitulatif:', error);
    }
  };

  // Fonction getStatusColor supprimée (colonne statut n'existe plus)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Gestion des factures</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleMonthlyInvoiceClick}
            sx={{ mr: 2 }}
          >
            Facturation Mensuelle
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={handleRecapDialogOpen}
            sx={{ mr: 2 }}
          >
            Récapitulatif Fiscal
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/factures/nouvelle')}
          >
            Nouvelle facture
          </Button>
        </Box>
      </Box>

      {/* Champ de recherche avec autocomplete */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <SearchIcon sx={{ color: 'action.active' }} />
        <Autocomplete
          freeSolo
          options={searchOptions}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.label || '';
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body2">
                  {option.label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {option.type === 'numero' ? 'Numéro de facture' : 'Nom du client'}
                </Typography>
              </Box>
            </Box>
          )}
          value={searchTerm}
          onChange={handleSearchChange}
          onInputChange={(event, newInputValue) => {
            setSearchTerm(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Rechercher par numéro de facture ou nom de client..."
              size="small"
            />
          )}
          sx={{ maxWidth: 400, minWidth: 300, flex: '1 1 280px' }}
          noOptionsText="Aucune correspondance trouvée"
          loadingText="Chargement..."
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filter-maison-label">Maison</InputLabel>
          <Select
            labelId="filter-maison-label"
            value={filterMaison}
            label="Maison"
            onChange={(e) => setFilterMaison(e.target.value)}
          >
            <MenuItem value="">Toutes les maisons</MenuItem>
            <MenuItem value="__EMPTY__">Sans maison (facture / client)</MenuItem>
            {maisonFilterChoices.map((nom) => (
              <MenuItem key={nom} value={nom}>
                {nom}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', flexGrow: 1 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Numéro</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Maison de repos</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Échéance</TableCell>
                <TableCell>Montant TTC</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFactures.map((facture) => (
                <TableRow key={facture.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                      {facture.numero}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {facture.client_prenom ? `${facture.client_prenom} ${facture.client_nom}` : facture.client_nom}
                    </Typography>
                    {facture.client_email && (
                      <Typography variant="body2" color="textSecondary">
                        {facture.client_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {effectiveMaisonRepos(facture) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {facture.date_facture
                      ? format(new Date(`${String(facture.date_facture).slice(0, 10)}T12:00:00`), 'dd/MM/yyyy', {
                          locale: fr,
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {facture.date_echeance
                      ? format(new Date(`${String(facture.date_echeance).slice(0, 10)}T12:00:00`), 'dd/MM/yyyy', {
                          locale: fr,
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {facture.total_ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewFacture(facture.id)}
                      title="Voir la facture"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadPDF(facture.id)}
                      title="Télécharger PDF"
                    >
                      <GetAppIcon />
                    </IconButton>
                    {canDeleteFactures && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDialogOpen(facture)}
                        title="Supprimer la facture"
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {filteredFactures.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 8 }}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {searchTerm || filterMaison
              ? 'Aucune facture ne correspond aux filtres (recherche ou maison). Essayez « Toutes les maisons » ou videz la recherche.'
              : 'Aucune facture trouvée'}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Commencez par créer votre première facture
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/factures/nouvelle')}
          >
            Créer une facture
          </Button>
        </Box>
      )}

      {/* Menu pour changer le statut */}
      {/* Menu de statut supprimé (colonne statut n'existe plus) */}

      {/* Dialog pour le récapitulatif PDF */}
      <Dialog open={recapDialog} onClose={handleRecapDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Générer un récapitulatif PDF</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Période</InputLabel>
              <Select
                value={recapData.periode}
                label="Période"
                onChange={(e) => handleRecapDataChange('periode', e.target.value)}
              >
                {periodes.map((periode) => (
                  <MenuItem key={periode.value} value={periode.value}>
                    {periode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {recapData.periode === 'mois' ? (
              // Sélecteurs pour mois et année
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Mois</InputLabel>
                  <Select
                    value={recapData.mois}
                    label="Mois"
                    onChange={(e) => handleRecapDataChange('mois', e.target.value)}
                  >
                    {moisOptions.map((mois) => (
                      <MenuItem key={mois.value} value={mois.value}>
                        {mois.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Année</InputLabel>
                  <Select
                    value={recapData.annee}
                    label="Année"
                    onChange={(e) => handleRecapDataChange('annee', e.target.value)}
                  >
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - 5 + i;
                      return (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              // DatePicker pour les autres périodes
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DatePicker
                  label="Date de référence"
                  value={recapData.date}
                  onChange={(newValue) => handleRecapDataChange('date', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRecapDialogClose}>Annuler</Button>
          <Button onClick={handleGenerateRecap} variant="contained">
            Générer le PDF
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Dialogue pour la facturation journalière */}
      <Dialog open={monthlyInvoiceDialog} onClose={() => setMonthlyInvoiceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Facturation Mensuelle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {monthlyInvoiceStats && (
              <Alert severity={monthlyInvoiceStats.total > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{format(monthlyInvoiceDate, 'MMMM yyyy', { locale: fr })}</strong> :{' '}
                  {monthlyInvoiceStats.total} facture(s) enregistrée(s).
                </Typography>
                {monthlyInvoiceStats.total > 0 && (
                  <Typography variant="caption" component="div" sx={{ mt: 1, display: 'block' }}>
                    {Object.entries(monthlyInvoiceStats.byMaison || {}).map(([nom, n]) => (
                      <span key={nom} style={{ display: 'block' }}>
                        · {nom} : {n}
                      </span>
                    ))}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Pour le PDF, choisissez la même maison que sur les factures de ce mois (ou « Toutes les maisons »).
                </Typography>
              </Alert>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <DatePicker
                label="Mois"
                value={monthlyInvoiceDate}
                onChange={(newValue) => setMonthlyInvoiceDate(newValue)}
                views={['year', 'month']}
                openTo="month"
                renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
              />
            </LocalizationProvider>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Maison de repos</InputLabel>
              <Select
                value={monthlyInvoiceMaison}
                onChange={(e) => setMonthlyInvoiceMaison(e.target.value)}
                label="Maison de repos"
              >
                <MenuItem value="">Toutes les maisons</MenuItem>
                {monthlyMaisonsOptions.map((nom) => (
                  <MenuItem key={nom} value={nom}>
                    {nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonthlyInvoiceDialog(false)}>Annuler</Button>
          <Button onClick={handleDownloadMonthlyInvoice} variant="contained">
            Générer PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          {deleteDialog.facture && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Cette action est irréversible !
              </Alert>
              <Typography variant="body1" gutterBottom>
                Êtes-vous sûr de vouloir supprimer cette facture ?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Facture : {deleteDialog.facture.numero}
                </Typography>
                <Typography variant="body2">
                  Client : {deleteDialog.facture.client_prenom ? 
                    `${deleteDialog.facture.client_prenom} ${deleteDialog.facture.client_nom}` : 
                    deleteDialog.facture.client_nom}
                </Typography>
                <Typography variant="body2">
                  Montant : {deleteDialog.facture.total_ttc?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </Typography>
                <Typography variant="body2">
                  Date : {new Date(deleteDialog.facture.date_facture).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteFacture} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Footer avec mention Woud */}
      <Footer />
    </Box>
  );
}

export default Factures; 